import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useDashboardPermissions } from '@components'
import { useMediaQueryContext } from '@fourthwall/components'
import { useOnceInView, useTagManager } from '@hooks'
import { routing } from '@utils/routing'

import { useRecommendationsDismiss } from '../../../Recommendations/hooks/useRecommendationsDismiss'
import type { MiniRecommendationSlideProps } from '../../MiniRecommendations.type'
import * as Carousel from '../../components/Carousel/Carousel'
import { MiniRecommendationDismissButton } from '../../components/MiniRecommendationDismissButton/MiniRecommendationDismissButton'

const ID = 'thankSupporters'

const CarouselItem = styled(Carousel.Item)`
  background-color: rgba(255, 68, 162, 0.15);

  &:hover {
    background-color: rgba(255, 68, 162, 0.25);
  }
`

export const ThankSupportersSlide = ({
  data,
  'data-role': dataRole,
  resetSlider,
}: MiniRecommendationSlideProps) => {
  const { tablet } = useMediaQueryContext()
  const { thankYous } = data
  const { dashboardPermissions } = useDashboardPermissions()
  const tagManager = useTagManager()
  const navigate = useNavigate()
  const { recommendationsDismiss } = useRecommendationsDismiss()
  const viewRef = useOnceInView(() => {
    tagManager.trigger('mini_recommendation_view', {
      type: ID,
    })
  })

  if (!dashboardPermissions.contributions.thankYous) {
    return null
  }

  if (!thankYous.kudosOrdersCount?.count || !recommendationsDismiss.thankYou.visible) {
    return null
  }

  const redirectToThankYous = () => {
    tagManager.trigger('mini_recommendation_click', {
      type: ID,
    })
    navigate(routing.contributions.thankYous.self)
  }

  return (
    <CarouselItem
      ref={viewRef}
      data-role={dataRole}
      data-analytics={ID}
      onClick={redirectToThankYous}
    >
      <Carousel.ContentLeft>
        <Carousel.ItemEmote>💌</Carousel.ItemEmote>
        {!tablet && <Carousel.ItemText>Thank your supporters</Carousel.ItemText>}
        {tablet && <Carousel.ItemText>Thank your top supporters</Carousel.ItemText>}
      </Carousel.ContentLeft>

      {recommendationsDismiss.thankYou.visible && (
        <MiniRecommendationDismissButton
          onDismiss={() => {
            recommendationsDismiss.thankYou.dismiss()
            tagManager.trigger('mini_recommendation_dismiss', {
              type: ID,
            })
            setTimeout(() => {
              resetSlider()
            }, 0)
          }}
        />
      )}
    </CarouselItem>
  )
}
